<template>
  <b-card class="search-parts-page">
    <b-row>
      <!-- Left Column -->
      <b-col md="8">
        <h4 class="mb-3 font-weight-bold">Cart</h4>

        <!-- Parts Search -->
        <b-row class="mb-3 align-items-center">
          <b-col md="4" class="pl-0">
            <label class="mb-0 text-nowrap">
              Parts Search:
              <span class="required-asterisk">*</span>
            </label>
          </b-col>

          <b-col md="8">
            <v-select
              v-model="selectedPart"
              :disabled="submitInProgress || getPartQuoteInProgress"
              :options="partList"
              :filterable="false"
              :loading="loading"
              placeholder="Enter part number or part description here..."
              label="partNumber"
              @search="searchParts"
              @input="addPart"
            >
              <template #option="option">
                {{ partDisplay(option) }}
              </template>

              <template #selected-option="option">
                {{ partDisplay(option) }}
              </template>
            </v-select>
          </b-col>
        </b-row>

        <b-row v-if="getPartQuoteInProgress" class="mb-3">
          <b-col>
            <div class="d-flex align-items-center text-primary">
              <b-spinner small class="mr-2"></b-spinner>
              <span>Getting part quote...</span>
            </div>
          </b-col>
        </b-row>

        <!-- Cart Header -->
        <div v-if="cartItems.length" class="py-2 border-bottom">
          <b-row no-gutters class="font-weight-bold text-muted">
            <b-col md="2">Part #</b-col>
            <b-col md="4">Description</b-col>
            <b-col md="2">Unit Price</b-col>
            <b-col md="2">Qty</b-col>
            <b-col md="2">Total</b-col>
          </b-row>
        </div>

        <!-- Cart Rows -->
        <div v-for="(part, index) in cartItems" :key="`${part.partNumber}-${index}`" class="py-2 border-bottom">
          <b-row no-gutters class="align-items-center">
            <b-col md="2" class="part-number text-nowrap pr-2">
              {{ part.partNumber }}
            </b-col>

            <b-col md="4" class="description pr-2">
              {{ part.description }}

              <font-awesome-icon
                v-if="isRetranPart(part)"
                :id="`retran-${index}`"
                icon="tag"
                class="ml-2 retran-indicator"
              />

              <b-tooltip v-if="isRetranPart(part)" :target="`retran-${index}`" placement="top">
                Eligible for Allison ReTran special pricing.
              </b-tooltip>
            </b-col>

            <b-col md="2" class="text-nowrap pr-2">
              {{
                part.price.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD'
                })
              }}
            </b-col>

            <b-col md="2" class="pr-2">
              <div class="d-flex align-items-center">
                <b-form-input
                  v-if="!part.IsCore"
                  v-model.number="part.quantity"
                  :disabled="submitInProgress"
                  type="number"
                  min="0"
                  step="1"
                  size="sm"
                  @input="syncCorePartQuantities(part)"
                />

                <span v-else>{{ part.quantity }}</span>

                <font-awesome-icon
                  v-if="!part.IsCore && getBackorderQty(part) > 0"
                  :id="`backorder-${index}`"
                  icon="exclamation-circle"
                  class="backorder-warning text-warning ml-2"
                />

                <b-tooltip
                  v-if="!part.IsCore && getBackorderQty(part) > 0"
                  :target="`backorder-${index}`"
                  placement="top"
                >
                  Ordered: {{ part.quantity }}
                  <br />
                  Available: {{ part.quantityOnHand }}
                  <br />
                  Backordered: {{ getBackorderQty(part) }}
                </b-tooltip>
              </div>
            </b-col>

            <b-col md="2">
              <div class="d-flex align-items-center justify-content-between">
                <span class="text-nowrap">
                  {{
                    (part.price * part.quantity).toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD'
                    })
                  }}
                </span>

                <b-button v-if="!part.IsCore" size="sm" variant="outline-danger" @click="removePart(index)">
                  <font-awesome-icon icon="trash-alt" />
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>

        <!-- Subtotal -->
        <div v-if="cartItems.length" class="my-3 fw-bold">
          SUBTOTAL:
          {{
            subtotal.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD'
            })
          }}
        </div>

        <div v-if="hasRetranParts" class="mb-3 retran-legend small">
          <font-awesome-icon icon="tag" class="mr-2 retran-indicator" />
          The part may qualify for the Allison ReTran program and be subject to special pricing. Add the part to the
          order, then contact the branch for more information and final pricing.
        </div>

        <!-- Shipping Message -->
        <div v-if="cartItems.length" class="mb-3 text-muted font-italic small">
          * SHIPPING AND TAX WILL BE CALCULATED SEPARATELY AFTER ORDER SUBMISSION
        </div>

        <!-- Shipping -->
        <h4 class="mb-3 font-weight-bold">Shipping</h4>

        <b-row class="mb-3 align-items-center">
          <b-col md="4" class="pl-0">
            <label class="mb-0 text-nowrap">
              Select Shipping Option:
              <span class="required-asterisk">*</span>
            </label>
          </b-col>

          <b-col md="8">
            <b-form-select
              v-model="selectedShipping"
              class="shipping-select"
              :disabled="submitInProgress"
              :options="shippingOptions"
            >
              <template #first>
                <b-form-select-option :value="''" disabled>Select an option</b-form-select-option>
              </template>
            </b-form-select>
          </b-col>
        </b-row>

        <!-- Comments -->
        <b-row class="mb-4">
          <b-col md="4" class="pl-0">
            <label class="mb-0 text-nowrap">Comments:</label>
          </b-col>

          <b-col md="8">
            <b-form-textarea v-model="comments" :disabled="submitInProgress" rows="3" />
          </b-col>
        </b-row>

        <!-- Purchase Order -->
        <h4 class="mb-3 font-weight-bold">Purchase Order</h4>

        <b-row class="align-items-center">
          <b-col md="4" class="pl-0">
            <label class="mb-0 text-nowrap">
              Enter Purchase Order:
              <span class="required-asterisk">*</span>
            </label>
          </b-col>

          <b-col md="8">
            <b-form-input v-model="purchaseOrder" :disabled="submitInProgress" type="text" />
          </b-col>

          <b-button
            class="mt-3"
            :disabled="!isSubmitEnabled || submitInProgress"
            type="submit"
            variant="primary"
            @click="submitOrder()"
          >
            <template v-if="submitInProgress">
              <b-spinner small class="mr-2" />
              SUBMITTING...
            </template>
            <template v-else>SUBMIT ORDER</template>
          </b-button>
        </b-row>
      </b-col>

      <!-- Right Column -->
      <b-col md="4">
        <b-card no-body class="mb-2">
          <b-card-header
            v-b-toggle.shipping-address
            class="
              d-flex
              justify-content-between
              align-items-center
              text-primary
              font-weight-bold
              text-uppercase
              bg-white
              border-bottom
            "
            style="cursor: pointer"
          >
            <span>Shipping Information</span>
            <font-awesome-icon icon="chevron-right" :class="{ rotated: shippingAddressOpen }" />
          </b-card-header>

          <b-collapse id="shipping-address" v-model="shippingAddressOpen">
            <b-card-body>
              <b-row class="mb-2">
                <b-col cols="5" class="font-weight-bold text-nowrap">Company Name:</b-col>
                <b-col>
                  {{ customer.customerName }}
                </b-col>
              </b-row>

              <b-row class="mb-2">
                <b-col cols="5" class="font-weight-bold text-nowrap">Recipient:</b-col>
                <b-col>
                  {{ user.name }}
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="5" class="font-weight-bold text-nowrap">Shipping Address:</b-col>
                <b-col>
                  {{ customer.address1 }}
                  <template v-if="customer.address2">
                    {{ customer.address2 }}
                  </template>
                  <br />
                  {{ customer.city }}, {{ customer.state }} {{ customer.zipCode }}
                </b-col>
              </b-row>
            </b-card-body>
          </b-collapse>
        </b-card>

        <b-card no-body>
          <b-card-header
            v-b-toggle.branch-contact
            class="
              d-flex
              justify-content-between
              align-items-center
              text-primary
              font-weight-bold
              text-uppercase
              bg-white
              border-bottom
            "
            style="cursor: pointer"
          >
            <span>Branch Contact Information</span>

            <font-awesome-icon icon="chevron-right" :class="{ rotated: branchInfoOpen }" />
          </b-card-header>

          <b-collapse id="branch-contact" v-model="branchInfoOpen">
            <b-card-body>
              <b-row class="mb-2">
                <b-col cols="4" class="font-weight-bold text-nowrap">Branch Name:</b-col>
                <b-col>{{ branch.branchId }} - {{ branch.branchName }}</b-col>
              </b-row>

              <b-row class="mb-2">
                <b-col cols="4" class="font-weight-bold text-nowrap">Address:</b-col>
                <b-col class="text-break">
                  {{ branch.mailAddress }}
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="4" class="font-weight-bold text-nowrap">Phone:</b-col>
                <b-col>
                  {{ branch.phoneNumber | phone }}
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col class="text-center">
                  <b-button variant="outline-primary" size="sm" @click="showBranchMessageModal = true">
                    SEND MESSAGE TO BRANCH
                  </b-button>
                </b-col>
              </b-row>
            </b-card-body>
          </b-collapse>
        </b-card>
      </b-col>
    </b-row>
    <b-modal
      v-model="showOrderConfirmation"
      centered
      hide-footer
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      header-bg-variant="success"
      header-text-variant="light"
      title="Order Confirmation"
    >
      <div class="text-center py-4">
        <p>
          Thank you very much for shopping with Clarke Power Services. Your order has been successfully submitted and
          your ticket # is
          <strong>{{ submitSuccessfulId }}</strong>
          .
        </p>

        <p>
          See
          <b>Order History</b>
          tab for status updates.
        </p>
        <b-button variant="outline-primary" @click="showOrderConfirmation = false">CLOSE</b-button>
      </div>
    </b-modal>
    <b-modal
      v-model="showOrderFailure"
      centered
      hide-footer
      title="Order Submission Failed"
      header-bg-variant="danger"
      header-text-variant="light"
    >
      <div class="text-center py-4">
        <p>We were unable to submit your order at this time.</p>

        <p v-if="submitErrorMessage" class="text-muted">
          {{ submitErrorMessage }}
        </p>

        <p>Please try again later or contact support if the issue persists.</p>

        <b-button variant="outline-danger" @click="showOrderFailure = false">CLOSE</b-button>
      </div>
    </b-modal>
    <b-modal
      v-model="showBranchMessageModal"
      title="Send Message to Branch"
      :ok-disabled="branchMessageInProgress"
      :cancel-disabled="branchMessageInProgress"
      :ok-title="branchMessageInProgress ? 'Sending...' : 'Send'"
      @ok="sendBranchMessage"
    >
      <b-form-group label="Message">
        <b-form-textarea
          v-model="branchMessage"
          :disabled="branchMessageInProgress"
          rows="5"
          maxlength="1000"
          placeholder="Enter your message to the branch..."
        />
      </b-form-group>

      <div v-if="branchMessageInProgress" class="d-flex align-items-center text-primary mt-2">
        <b-spinner small class="mr-2" />
        <span>Sending message...</span>
      </div>
    </b-modal>
    <b-modal
      v-model="showBranchMessageFailure"
      centered
      hide-footer
      title="Branch Message Failed"
      header-bg-variant="danger"
      header-text-variant="light"
    >
      <div class="text-center py-4">
        <p>We were unable to send your message to the branch at this time.</p>

        <p v-if="branchMessageErrorMessage" class="text-muted">
          {{ branchMessageErrorMessage }}
        </p>

        <p>Please try again later or contact support if the issue persists.</p>

        <b-button variant="outline-danger" @click="showBranchMessageFailure = false">CLOSE</b-button>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { CusEcommerceActions, CusEcommerceGetters, CusEcommerceMutations } from '@/shared/store/cus-ecommerce/types';
import { LookupGetters } from '@/shared/store/lookup/types';
import { UserGetters } from '@/shared/store/user/types';
// helpers
import SuccessService from '@/shared/services/SuccessService';
import vSelect from 'vue-select';

export default {
  components: {
    vSelect
  },

  data() {
    return {
      branchInfoOpen: true,
      branchMessage: '',
      branchMessageErrorMessage: '',
      branchMessageInProgress: false,
      cartItems: [],
      comments: '',
      debounceTimer: null,
      getPartQuoteInProgress: false,
      loading: false,
      purchaseOrder: '',
      selectedPart: null,
      selectedShipping: '',
      shippingAddressOpen: true,
      shippingOptions: [
        { value: 'pickup', text: 'Pick Up at Branch' },
        { value: 'ground', text: 'Ground' },
        { value: 'nextDayAir', text: 'Next Day Air' },
        { value: 'collect', text: 'Collect' }
      ],
      showBranchMessageModal: false,
      showBranchMessageFailure: false,
      showOrderConfirmation: false,
      showOrderFailure: false,
      submitErrorMessage: '',
      submitInProgress: false,
      submitSuccessfulId: ''
    };
  },

  computed: {
    ...mapGetters({
      branch: LookupGetters.GET_ECOMMERCE_BRANCH_DETAILS,
      customer: LookupGetters.GET_ECOMMERCE_CUSTOMER,
      partList: CusEcommerceGetters.GET_PARTS,
      user: UserGetters.GET_USER_PROFILE
    }),

    subtotal() {
      let sum = 0;
      for (const part of this.cartItems) {
        sum += part.price * part.quantity;
      }
      return sum;
    },

    hasRetranParts() {
      return this.cartItems.some(part => this.isRetranPart(part));
    },

    isSubmitEnabled() {
      if (this.cartItems.length == 0) {
        return false;
      }
      if (this.selectedShipping.length == 0) {
        return false;
      }
      if (this.purchaseOrder.length == 0) {
        return false;
      }
      return true;
    }
  },

  methods: {
    ...mapActions({
      getPartQuoteAction: CusEcommerceActions.GET_PART_QUOTE,
      searchPartsAction: CusEcommerceActions.SEARCH_PARTS,
      sendMessageAction: CusEcommerceActions.SEND_MESSAGE,
      submitPartsAction: CusEcommerceActions.SUBMIT_PARTS
    }),

    ...mapMutations({
      setParts: CusEcommerceMutations.SET_PART_LIST
    }),

    partDisplay(part) {
      return `${part.vendorPartId} - ${part.description.toUpperCase()} - (On Hand: ${part.quantityOnHand})`;
    },

    isRetranPart(part) {
      return Boolean(part?.isRetran);
    },

    getBackorderQty(part) {
      return Math.max(part.quantity - part.quantityOnHand, 0);
    },

    searchParts(search, loading) {
      clearTimeout(this.debounceTimer);

      if (!search || search.length < 2) {
        return;
      }

      this.debounceTimer = setTimeout(async () => {
        loading(true);

        try {
          await this.searchPartsAction({
            branchId: this.customer.branchId,
            search
          });
        } finally {
          loading(false);
        }
      }, 300);
    },

    async addPart(part) {
      if (!part) {
        return;
      }

      // get part quote
      this.getPartQuoteInProgress = true;
      const response = await this.getPartQuoteAction({
        branchId: this.customer.branchId,
        customerId: this.customer.customerId,
        part
      });

      if (response?.status == 200) {
        response.data.forEach((quote, index) => {
          const quotedPart = {
            ...part,
            ...(index > 0 ? quote.availableParts[0] : {}),
            IsCore: index === 1,
            coreForPartNumber: part.partNumber,
            price: quote.priceSuggested,
            quantityOnHand: quote.quantityOnHand
          };

          const existing =
            index === 0 && this.cartItems.find(item => item.partNumber === quotedPart.partNumber && !item.IsCore);

          if (existing) {
            existing.quantity += 1;
            this.cartItems = this.cartItems.filter(
              item => !item.IsCore || item.coreForPartNumber !== quotedPart.partNumber
            );
          } else {
            this.cartItems.push({
              ...quotedPart,
              quantity: index > 0 ? this.getPrimaryPartQuantity(quotedPart) : 1
            });
          }
        });

        this.selectedPart = null;
        this.setParts([]);
      }
      this.getPartQuoteInProgress = false;
    },

    removePart(index) {
      const removedPart = this.cartItems[index];

      this.cartItems = this.cartItems.filter(
        part => part !== removedPart && !(part.IsCore && part.coreForPartNumber === removedPart.partNumber)
      );
    },

    getPrimaryPartQuantity(part) {
      return this.cartItems.find(item => item.partNumber === part.coreForPartNumber && !item.IsCore)?.quantity || 1;
    },

    syncCorePartQuantities(primaryPart) {
      this.cartItems
        .filter(part => part.IsCore && part.coreForPartNumber === primaryPart.partNumber)
        .forEach(part => {
          part.quantity = primaryPart.quantity;
        });
    },

    async submitOrder() {
      const formattedPayload = {
        customerId: this.customer.customerId,
        customerName: this.customer.customerName,
        name: this.user.name,
        email: this.user.email,
        orderId: '',
        branchId: this.customer.branchId,
        purchaseOrder: this.purchaseOrder,
        phone: '555555555', // to be replaced with an actual value later
        shippingOption: this.selectedShipping,
        items: [],
        comment: this.comments
      };
      for (var i = 0; i < this.cartItems.length; i++) {
        formattedPayload.items.push({
          ItemId: this.cartItems[i].partNumber,
          orderQty: this.cartItems[i].quantity,
          unitCost: this.cartItems[i].price,
          VenPrtId: this.cartItems[i].vendorPartId,
          IsRetran: this.cartItems[i].isRetran,
          IsCore: this.cartItems[i].IsCore
        });
      }

      this.submitInProgress = true;
      this.submitErrorMessage = '';
      try {
        const response = await this.submitPartsAction(formattedPayload);

        if (response?.status == 200) {
          this.submitSuccessfulId = response.data.id;
          this.showOrderConfirmation = true;

          // clear inputs
          this.comments = '';
          this.cartItems = [];
          this.selectedShipping = '';
          this.purchaseOrder = '';
          this.setParts([]);
        } else {
          this.submitErrorMessage = response?.data?.message || 'The server returned an unexpected response.';
          this.showOrderFailure = true;
        }
      } catch (error) {
        this.submitErrorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'An unexpected error occurred while submitting your order.';
        this.showOrderFailure = true;
      } finally {
        this.submitInProgress = false;
      }
    },

    async sendBranchMessage(bvModalEvt) {
      bvModalEvt.preventDefault();

      const payload = {
        branchId: this.branch.branchId,
        userName: this.user.name,
        email: this.user.email,
        phoneNumber: this.customer.phone,
        customerName: this.customer.customerName,
        customerId: this.customer.customerId,
        message: this.branchMessage
      };

      this.branchMessageInProgress = true;
      this.branchMessageErrorMessage = '';

      try {
        const response = await this.sendMessageAction(payload);

        if (response?.status === 200 || response?.status === 204) {
          this.branchMessage = '';
          this.showBranchMessageModal = false;

          SuccessService.createSuccessToast(this.$root, 'Message sent successfully.');
        } else {
          this.branchMessageErrorMessage = response?.data?.message || 'The server returned an unexpected response.';

          this.showBranchMessageFailure = true;
        }
      } catch (error) {
        this.branchMessageErrorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'An unexpected error occurred while sending your message.';

        this.showBranchMessageFailure = true;
      } finally {
        this.branchMessageInProgress = false;
      }
    }
  }
};
</script>

<style scoped>
.search-parts-page {
  overflow: auto;
  padding: 1rem;
}

.part-number {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.description {
  overflow-wrap: anywhere;
}

.retran-indicator {
  color: #5f7285;
  font-size: 0.85rem;
  vertical-align: middle;
}

.retran-legend {
  color: #6c757d;
  line-height: 1.4;
}

.modal-header {
  background-color: #28a745;
  color: white;
}

.shipping-select {
  color: #212529;
  font-weight: 400;
}

.rotated {
  transform: rotate(90deg);
}

.rotated,
.fa-chevron-right {
  transition: transform 0.2s ease-in-out;
}

.backorder-warning {
  cursor: help;
  font-size: 0.9rem;
}
</style>
